import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import { ANALYTICS_GRAPH_TYPES } from './graphCatalog';

const formatValue = (value) => Number(value).toLocaleString();

const buildSmoothPath = (points = []) => {
  if (!points.length) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  return points.reduce((path, point, index) => {
    if (index === 0) return `M ${point.x} ${point.y}`;
    const previous = points[index - 1];
    const controlX = (previous.x + point.x) / 2;
    return `${path} C ${controlX} ${previous.y}, ${controlX} ${point.y}, ${point.x} ${point.y}`;
  }, '');
};

const GoalRing = ({ item }) => {
  useUiLanguage();
  const progress = Math.max(0, Math.min(100, Math.round(item.percentComplete || 0)));
  const tone = item.actual >= item.target ? 'track' : 'critical';

  return (
    <div className="executive-overview-target-item analytics-goal-ring-item">
      <div
        className={`executive-overview-ring ${tone}`}
        style={{ '--progress': `${progress}%` }}
      >
        <div className="executive-overview-ring-inner">{progress}%</div>
      </div>
      <div className="executive-overview-target-label analytics-goal-ring-label">{uiText(item.label)}</div>
      <div className="executive-overview-target-value analytics-goal-ring-value">
        {formatValue(item.actual)}/{formatValue(item.target)}
      </div>
    </div>
  );
};

const GoalProgressVisual = ({ model, isRTL = false }) => {
  useUiLanguage();
  const dataset = model.dataset;
  const sizeClass = model.size || 'medium';
  const items = dataset?.items || [];
  const periods = dataset?.periods || [];
  const [selectedItemId, setSelectedItemId] = useState(items[0]?.id || '');

  useEffect(() => {
    if (!items.some((item) => item.id === selectedItemId)) {
      setSelectedItemId(items[0]?.id || '');
    }
  }, [items, selectedItemId]);

  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedItemId) || items[0] || null,
    [items, selectedItemId]
  );

  if (!items.length) return null;

  if (model.activeGraph === ANALYTICS_GRAPH_TYPES.RADIAL_RINGS) {
    return (
      <div className={`analytics-visual analytics-visual-goal-rings ${sizeClass}${isRTL ? ' rtl' : ''}`}>
        <div className="analytics-goal-rings-grid">
          {items.map((item) => (
            <GoalRing key={item.id} item={item} />
          ))}
        </div>
      </div>
    );
  }

  if (model.activeGraph === ANALYTICS_GRAPH_TYPES.GOAL_LABEL_TREND && selectedItem) {
    const maxValue = Math.max(
      ...selectedItem.history.flatMap((entry) => [entry.target, entry.actual]),
      1
    );
    const chartWidth = 520;
    const chartHeight = 220;
    const points = selectedItem.history.map((entry, index) => {
      const x = 32 + (index / Math.max(selectedItem.history.length - 1, 1)) * (chartWidth - 64);
      const targetY = chartHeight - ((entry.target / maxValue) * 150) - 28;
      const actualY = chartHeight - ((entry.actual / maxValue) * 150) - 28;
      return {
        ...entry,
        x,
        targetY,
        actualY,
        met: entry.actual >= entry.target
      };
    });
    const targetPath = buildSmoothPath(points.map((point) => ({ x: point.x, y: point.targetY })));

    return (
      <div className={`analytics-visual analytics-visual-goal-trend ${sizeClass}`}>
        <div className="analytics-goal-trend-toolbar">
          <div className="analytics-goal-trend-summary">
            <span className="analytics-visual-sub">
              {isRTL ? 'التتبع حسب الوسم' : 'Tracking by label'}
            </span>
          </div>
          <label className="analytics-goal-select-wrap">
            <span className="sr-only">{isRTL ? 'اختر الوسم' : 'Select label'}</span>
            <select
              className="analytics-goal-select"
              value={selectedItem.id}
              onChange={(event) => setSelectedItemId(event.target.value)}
            >
              {items.map((item) => (
                <option key={item.id} value={item.id}>
                  {uiText(item.label)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <svg viewBox={`0 0 ${chartWidth} ${chartHeight + 22}`} className="analytics-goal-trend-svg" role="img" aria-label={uiText(selectedItem.label)}>
          <line x1="28" y1={chartHeight} x2={chartWidth - 20} y2={chartHeight} className="analytics-visual-axis" />
          <path d={targetPath} className="analytics-goal-target-path" />
          {points.map((point) => (
            <g key={point.periodKey}>
              <circle
                cx={point.x}
                cy={point.actualY}
                r="6"
                className={`analytics-goal-dot ${point.met ? 'success' : 'danger'}`}
              />
              <text x={point.x} y={chartHeight + 18} textAnchor="middle" className="analytics-goal-period-label">
                {periods.find((period) => period.key === point.periodKey)?.label || point.periodKey}
              </text>
            </g>
          ))}
        </svg>
      </div>
    );
  }

  const maxValue = Math.max(...items.flatMap((item) => [item.target, item.actual]), 1);

  return (
    <div className={`analytics-visual analytics-visual-goal-track ${sizeClass}`}>
      <svg viewBox="0 0 560 250" className="analytics-goal-track-svg" role="img" aria-label={model.title}>
        <line x1="26" y1="212" x2="534" y2="212" className="analytics-visual-axis" />
        {items.map((item, index) => {
          const columnGap = 560 / Math.max(items.length, 1);
          const barWidth = Math.min(52, Math.max(34, columnGap * 0.38));
          const centerX = 72 + index * columnGap;
          const targetY = 206 - ((item.target / maxValue) * 150);
          const actualY = 206 - ((item.actual / maxValue) * 150);
          const barHeight = 206 - actualY;
          const met = item.actual >= item.target;

          return (
            <g key={item.id}>
              <line
                x1={centerX - (barWidth / 2) - 6}
                x2={centerX + (barWidth / 2) + 6}
                y1={targetY}
                y2={targetY}
                className={`analytics-goal-target-line ${met ? 'success' : 'danger'}`}
              />
              <rect
                x={centerX - (barWidth / 2)}
                y={actualY}
                width={barWidth}
                height={barHeight}
                rx="8"
                className={`analytics-goal-bar ${met ? 'success' : 'danger'}`}
              />
              <text x={centerX} y={actualY + barHeight - 5} textAnchor="middle" className="analytics-goal-bar-value">
                {formatValue(item.actual)}
              </text>
              <text x={centerX} y="234" textAnchor="middle" className="analytics-goal-bar-label">
                {uiText(item.label)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default GoalProgressVisual;
